import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { Stepper } from "@/components/Stepper";
import { UploadStep } from "@/components/UploadStep";
import { AssumptionsForm } from "@/components/AssumptionsForm";
import { KpiCards } from "@/components/KpiCards";
import { CollectionsCard } from "@/components/CollectionsCard";
import { BudgetChart } from "@/components/BudgetChart";
import { BudgetTable } from "@/components/BudgetTable";
import { GoalSeekPanel } from "@/components/GoalSeekPanel";
import { RollingForecastPanel } from "@/components/RollingForecastPanel";
import { UsaliCheckPanel } from "@/components/UsaliCheckPanel";
import { NarrativePanel } from "@/components/NarrativePanel";
import { ExportBar } from "@/components/ExportBar";
import { VersionsPanel } from "@/components/VersionsPanel";
import { ApprovalBar } from "@/components/ApprovalBar";
import { TrackingStep } from "@/components/TrackingStep";
import { AuditTrailPanel } from "@/components/AuditTrailPanel";
import { PerformancePanel } from "@/components/PerformancePanel";
import { ActionPlanPanel } from "@/components/ActionPlanPanel";
import { CapexPanel } from "@/components/CapexPanel";
import { DepartmentBar, useProjectAccess } from "@/components/DepartmentBar";
import { api, errorText } from "@/lib/api";
import { MONTHS } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import { ModelSelect, useModels, modelLabel } from "@/components/ModelSelect";

export default function ProjectPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const wantStep = params.get("step"), wantSheet = params.get("sheet"), wantPanel = params.get("panel");
  const [project, setProject] = useState(null);
  const [budget, setBudget] = useState(null);
  const [step, setStep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auditKey, setAuditKey] = useState(0);
  const { can } = useAuth();
  const [access, reloadAccess] = useProjectAccess(id, auditKey);

  const models = useModels();
  const changeModel = async (v) => {
    try {
      await api.patch(`/projects/${id}`, { ai_model: v });
      toast.success(`AI model set to ${modelLabel(v, models)}`);
      load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data);
    if (data.status === "generated") {
      const b = await api.get(`/projects/${id}/budget`);
      setBudget(b.data);
    }
    setStep((s) => s || (wantStep && data.status === "generated" ? wantStep : data.status === "generated" ? (data.has_tracking ? "tracking" : "performance") : data.status === "uploaded" && data.budget_source !== "file" ? "assumptions" : "upload"));
  }, [id, wantStep]);
  useEffect(() => {
    if (!wantStep) return;
    setStep(wantStep);
    if (!wantPanel) return;
    let tries = 0, stable = 0;
    const t = setInterval(() => {
      const el = document.getElementById(`${wantPanel}-panel`);
      tries += 1;
      if (el) {
        const top = el.getBoundingClientRect().top;
        if (Math.abs(top - 72) > 12) { window.scrollTo({ top: window.scrollY + top - 72, behavior: "smooth" }); stable = 0; }
        else stable += 1;
      }
      if (stable >= 6 || tries > 40) clearInterval(t);
    }, 300);
    return () => clearInterval(t);
  }, [wantStep, wantSheet, wantPanel]);

  useEffect(() => { load().catch((e) => toast.error(errorText(e))); }, [load]);

  const generate = async (assumptions) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/generate`, assumptions);
      setBudget(data);
      setAuditKey((k) => k + 1);
      setProject((p) => ({ ...p, assumptions, status: "generated" }));
      setStep("results");
      const pinned = data?.sheets?.reduce((n, s) => n + s.lines.filter((l) => l.overrides && Object.keys(l.overrides).length).length, 0) || 0;
      if (pinned) toast.warning(`Budget generated — ${pinned} line(s) keep manual overrides and ignore assumption changes. Use "Clear overrides" to let the assumptions apply everywhere.`, { duration: 8000 });
      else toast.success("Budget generated");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const override = async (lineId, month, value) => {
    try {
      const { data } = await api.put(`/projects/${id}/override`, { line_id: lineId, month, value });
      setBudget(data);
      setAuditKey((k) => k + 1);
      toast.success(value === null ? "Override removed" : `${MONTHS[month - 1]} cell updated`);
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const [ovBusy, setOvBusy] = useState(false);
  const batchOverride = async (target, lineId, values) => {
    setOvBusy(true);
    try {
      const { data } = await api.put(`/projects/${id}/overrides/batch`, { target, line_id: lineId, values });
      setBudget(data);
      setAuditKey((k) => k + 1);
      const n = Object.values(values).filter((v) => v !== null).length;
      toast.success(n ? `${n} ${target} month(s) overridden` : "Overrides removed");
      return true;
    } catch (e) {
      toast.error(errorText(e));
      return false;
    } finally { setOvBusy(false); }
  };

  const clearOverrides = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/overrides/clear`);
      setBudget(data);
      toast.success("All overrides reset");
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const setDriver = async (lineId, driver) => {
    if (driver === "__formula") return;
    try {
      const { data } = await api.put(`/projects/${id}/driver`, { line_id: lineId, driver });
      if (data?.sheets) setBudget(data);
      setAuditKey((k) => k + 1);
      toast.success("Driver updated and budget recalculated");
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const reloadVersions = async () => {
    const { data } = await api.get(`/projects/${id}/versions`);
    setProject((p) => ({ ...p, versions: data }));
  };

  if (!project || !step) return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="project-loading">Loading project...</div>;
  const departments = [...new Set(((project.dataset?.summary?.sheets) || []).map((s) => s.department).filter((d) => d && !["Summary P&L", "Statistics"].includes(d)))];

  const done = [];
  if (project.dataset) done.push("upload");
  if (project.status === "generated") done.push("assumptions", "results");
  if (project.has_tracking) done.push("tracking");
  const fromFile = project.budget_source === "file";
  const locked = ["submitted", "approved"].includes(project.approval?.status) || !can("edit") || fromFile;
  const hasOverrides = budget?.sheets?.some((s) => s.lines.some((l) => l.overrides && Object.keys(l.overrides).length));

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AppNav>
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" className="text-slate-500 hover:text-slate-900 shrink-0" data-testid="back-to-dashboard"><ChevronLeft size={18} /></Link>
          <div className="min-w-0 leading-tight">
            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold truncate" data-testid="project-hotel-name">{project.hotel_name}</p>
            <p className="font-heading font-semibold text-slate-900 truncate" data-testid="project-title">{project.name}</p>
          </div>
        </div>
      </AppNav>
      <main className="px-4 sm:px-8 lg:px-12 py-8 space-y-8">
        <div className="flex flex-col 2xl:flex-row 2xl:items-center justify-between gap-4 rise">
          <Stepper current={step} done={done} onSelect={(st) => { setStep(st); if (wantStep) setParams({}, { replace: true }); }} hide={fromFile ? ["assumptions"] : []} />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="project-ai-model">
              <span className="hidden sm:inline">AI model</span>
              <ModelSelect value={project.ai_model} onChange={changeModel} disabled={!can("edit")} testid="project-ai-model-select" className="h-8 w-[220px] text-xs bg-white" />
            </div>
            {project.carried_from && <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs text-sky-900" data-testid="carried-from-banner">Carried forward from <b>{project.carried_from.name}</b> ({project.carried_from.year}): opening replacement reserve <b>{Math.round(project.carried_from.reserve_opening).toLocaleString("en-US")}</b> IDR and <b>{project.carried_from.items}</b> open CAPEX item(s) — visible in Assumptions and the CAPEX panel once the budget is generated.</p>}
            {["results", "performance"].includes(step) && budget && <ExportBar projectId={id} project={project} onProjectChanged={() => load().catch(() => {})} hasOverrides={hasOverrides} onClearOverrides={clearOverrides} onEditAssumptions={fromFile ? null : () => setStep("assumptions")} />}
          </div>
        </div>

        {project.status === "generated" && <ApprovalBar projectId={id} approval={project.approval} canSubmit={!!budget} onChange={() => load()} />}
        {budget && <DepartmentBar access={access} projectId={id} canEdit={can("edit") && !locked} onChanged={() => { reloadAccess(); setAuditKey((k) => k + 1); }} />}

        {step === "upload" && !can("upload") && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="no-upload-permission">You do not have upload access. Ask your super admin for the "Upload data" permission.</p>}
        {step === "upload" && locked && project.approval && ["submitted", "approved"].includes(project.approval.status) && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="locked-notice">Budget is {project.approval.status} — reopen it from the approval bar to upload new data.</p>}
        {step === "assumptions" && locked && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="locked-notice">{can("edit") ? `Budget is ${project.approval.status}` : "You have read-only access"} — reopen it from the approval bar to change assumptions.</p>}
        {step === "tracking" && <div className="space-y-6">
          {project.approval?.status !== "approved" && !fromFile && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="tracking-not-approved-notice">Budget {project.budget_year} is not approved yet{project.approval?.status ? ` (status: ${project.approval.status})` : ""}. Budget vs Actual tracking and the rolling forecast are used once the budget is final and the budget year is running — the figures below compare against the current budget until then.</p>}
          <TrackingStep project={project} onVersionSaved={() => load()} />{budget && <RollingForecastPanel projectId={id} />}</div>}
        {step === "performance" && budget && (
          <div className="space-y-6">
            <PerformancePanel projectId={id} refreshKey={auditKey} />
            <ActionPlanPanel projectId={id} budgetYear={project.budget_year} access={access} onChanged={() => setAuditKey((k) => k + 1)} />
            {!fromFile && <CapexPanel projectId={id} budgetYear={project.budget_year} locked={locked} onBudgetChanged={() => { setAuditKey((k) => k + 1); load().catch(() => {}); }} />}
          </div>
        )}
        {step === "performance" && !budget && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="performance-no-budget">Generate the budget first to see the performance dashboard.</p>}
        {step === "upload" && (
          <UploadStep project={project} onHistoryChanged={() => load()} onUploaded={() => load().then(() => setStep(fromFile ? "results" : "assumptions"))} onNext={() => setStep(fromFile ? "results" : "assumptions")} disabled={!can("upload") || (project.approval && ["submitted", "approved"].includes(project.approval.status))} />
        )}
        {step === "assumptions" && (
          <AssumptionsForm key={project.id} initial={project.assumptions} onGenerate={generate} busy={busy || locked} departments={departments} projectId={id} budgetYear={project.budget_year} hotelName={project.hotel_name} />
        )}
        {step === "results" && budget && (
          <div className="space-y-6">
            {fromFile && <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="file-budget-notice">Budget {budget.budget_year} taken from the Budget column of the uploaded P&amp;L files (months without a file use the average of the uploaded months). Actuals are already loaded into Budget vs Actual.</p>}
            <KpiCards kpis={budget.kpis} budgetYear={budget.budget_year} notes={budget.usali_block?.notes} />
            <CollectionsCard collections={budget.collections} actualYear={budget.actual_year} budgetYear={budget.budget_year} />
            <div className="grid xl:grid-cols-[1fr_420px] gap-6">
              <BudgetChart kpis={budget.kpis} actualYear={budget.actual_year} budgetYear={budget.budget_year} lastMonth={budget.last_actual_month} />
              <NarrativePanel projectId={id} initial={project.narrative} modelLabel={modelLabel(project.ai_model, models)} canEdit={can("edit")} />
            </div>
            {!fromFile && <UsaliCheckPanel projectId={id} version={budget?.kpis?.gop?.budget_total} />}
            {can("edit") && !fromFile && <GoalSeekPanel projectId={id} budget={budget} locked={locked} onApplied={(b) => { setBudget(b); setAuditKey((k) => k + 1); load().catch(() => {}); }} />}
            <BudgetTable budget={budget} assumptions={project.assumptions} onOverride={override} onDriver={setDriver} readOnly={locked} onBatchOverride={batchOverride} canOverride={can("edit") && !fromFile} overrideBusy={ovBusy} projectId={id} editableSheets={access?.editable_sheets ?? null} payrollAll={!!access?.payroll_all} initialSheet={wantSheet} />
            <div className="grid xl:grid-cols-2 gap-6">
              {can("edit") && <VersionsPanel projectId={id} versions={project.versions || []} onChange={() => { reloadVersions(); setAuditKey((k) => k + 1); }} onRestored={(b) => { setBudget(b); load(); setAuditKey((k) => k + 1); }} />}
              <AuditTrailPanel projectId={id} refreshKey={auditKey + (project.approval?.history?.length || 0)} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
